<template>
  <div :style="style.container">
    <div :style="style.headerContainer">
      <RouterLinkBackPrimary
        :to="{
          name: 'teamup-team',
          params: {
            code: tenantTeam.code,
          }
        }"
        :style="style.linkBack"
      >
        Back to {{ tenantTeam.name }}
      </RouterLinkBackPrimary>
      <SearchExpertBoxPrimary
        v-model="tags"
        :disabled="isSearching"
        :initInput="initInput"
        placeholder="Search coworkers"
        @filled="searchBoxFilled = true"
        @unfilled="searchBoxFilled = false"
        @focusin="searchBoxFocus = true"
        @focusout="searchBoxFocus = false"
        @search-by-person-name="searchExpertByPersonName"
        @search-by-skill-name="searchExpertBySkillName"
        :style="getSearchBoxStyle"
      />
    </div>
    <div :style="style.searchResultsContainer">
      <template
        v-if="(
          isSearching ||
          !loadedTenantTeamPermission
        )"
      >
        <LoaderPrimary :style="style.loader" />
      </template>
      <template v-else-if="displayedExperts.length > 0">
        <div :style="style.innerContainer">
          <div :style="style.innerHeaderContainer">
            {{ experts.length }} result{{ experts.length > 1 ? 's' : '' }}
          </div>
          <div
            v-for="(expert, index) in displayedExperts"
            :key="index"
            :style="style.displayedExpertContainer"
          >
            <SearchedUserSimple
              v-if="!searchBySkill"
              :expert="expert"
              @add-to-team="addTenantUserToTenantTeam"
              @delete-from-team="deleteTenantTeamTenantUser"
              :style="style.searchedUser"
            />
            <SearchedUserVertical
              v-else-if="verticalLayout"
              :expert="expert"
              @add-to-team="addTenantUserToTenantTeam"
              @delete-from-team="deleteTenantTeamTenantUser"
              :style="style.searchedUser"
            />
            <SearchedUserHorizontal
              v-else
              :expert="expert"
              @add-to-team="addTenantUserToTenantTeam"
              @delete-from-team="deleteTenantTeamTenantUser"
              :style="style.searchedUser"
            />
          </div>
          <PaginationPrimary
            v-model="page"
            :pageNum="nPages"
            :style="style.pagination"
          />
        </div>
      </template>
      <template v-else>
        <div :style="style.placeholderContainer">
          <TextNoResult>
            No result found.<br>Try using different search words.
          </TextNoResult>
          <ImagePrimary
            :src="require('@img/robot.png')"
            :style="style.image"
          />
        </div>
      </template>
    </div>
  </div>
</template>

<script>
  import ApiExpert from '@js/api/expert'
  import ApiTenantTeamPermission from '@js/api/tenant-team-permission'
  import ApiTenantTeamTenantUser from '@js/api/tenant-team-tenant-user'
  import ApiTenantTeamView from '@js/api/tenant-team-view'
  import ImagePrimary from '@modules/image/image-primary'
  import LoaderPrimary from '@modules/loader/loader-primary'
  import PaginationPrimary from '@modules/pagination/pagination-primary'
  import RouterLinkBackPrimary from '@modules/link/router-link-back-primary'
  import SearchedUserHorizontal from '@teamup/modules/searched-user-horizontal'
  import SearchedUserSimple from '@teamup/modules/searched-user-simple'
  import SearchedUserVertical from '@teamup/modules/searched-user-vertical'
  import SearchExpertBoxPrimary from '@modules/v2/input/search-expert-box-primary'
  import TextNoResult from '@modules/text/text-no-result'
  import { Layout } from '@js/styles'
  import { PAGE_SIZES, SEARCH_EXPERT_PARAMS, Z_INDEXES } from '@js/constants'
  import { mapGetters, mapState } from 'vuex'
  import { parseRouteQuery } from '@js/search-utils'

  export default {
    Name: 'TeamupTeamSearchView',
    components: {
      ImagePrimary,
      LoaderPrimary,
      PaginationPrimary,
      RouterLinkBackPrimary,
      SearchedUserHorizontal,
      SearchedUserSimple,
      SearchedUserVertical,
      SearchExpertBoxPrimary,
      TextNoResult,
    },
    data: function () {
      return {
        experts: [],
        initInput: '',
        initTags: [],
        isSearching: false,
        nPages: 0,
        page: 1,
        pageSize: PAGE_SIZES.DEFAULT,
        searchBoxFilled: false,
        searchBoxFocus: false,
        searchBySkill: false,
        tags: [],
        verticalLayout: false,
        verticalLayoutWidthThresh: 860,
        style: {
          container: {
            margin: Layout.container.primary.margin,
            maxWidth: Layout.container.primary.maxWidth,
            padding: Layout.space.lg,
            width: '100%',
          },
          displayedExpertContainer: {
            width: '100%',
          },
          headerContainer: {
            maxWidth: '100%',
            width: '100%',
          },
          image: {
            height: '298px',
            margin: '51px 0 0 0',
            width: '400px',
          },
          innerContainer: {
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            width: '100%',
          },
          innerHeaderContainer: {
            margin: `${Layout.space.md} 0 0 0`,
            textAlign: 'left',
            width: '100%',
          },
          linkBack: {
            cursor: 'poitner',
          },
          loader: {
            height: '320px',
            width: '100%',
          },
          placeholderContainer: {
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            margin: '48px auto',
          },
          pagination: {
            margin: `${Layout.space.lg} 0 0 0`,
          },
          searchedUser: {
            margin: `${Layout.space.md} 0 0 0`,
          },
          searchResultsContainer: {},
        },
      };
    },
    created: async function () {
      if (this.loadedTenantTeamPermission && this.viewOnly) {
        this.goToTenantTeamSelectPageByError();
      }
    },
    mounted: async function () {
      this.verticalLayout = this.windowWidth < this.verticalLayoutWidthThresh;

      try {
        const { searchInput, searchBySkill } = await parseRouteQuery(this.$route.query, true);
        this.initInput = await searchBySkill ? '' : searchInput[0];
        this.initTags = await searchBySkill ? searchInput : [];
        this.tags = [...this.initTags];
        this.searchBySkill = await searchBySkill;
        await this.searchExpert(searchInput, searchBySkill);
      } catch (error) {
        console.error(error);
      }
    },
    computed: {
      ...mapGetters('teamup', ['loaded']),
      ...mapState({
        tenantTeam: state => state.teamup.data.tenantTeam,
        tenantTeamTenantUsers: state => state.teamup.data.tenantTeamTenantUsers,
        viewOnly: state => state.teamup.viewOnly,
        windowWidth: state => state.window.width,
      }),
      displayedExperts: function () {
        const indexStart = (this.page - 1) * this.pageSize;

        return this.experts.reduce((result, elem) => {
          const tenantTeamTenantUserId = this.getTenantTeamTenantUserId(elem.tenantUserId);
          result.push({
            ...elem,
            id: elem.tenantUserId,
            isTenantTeamTenantUser: tenantTeamTenantUserId != null ? true : false,
            tenantTeamTenantUserId: tenantTeamTenantUserId,
          });
          return result;
        }, []).splice(indexStart, this.pageSize);
      },
      getSearchBoxStyle: function () {
        return {
          flex: 1,
          margin: `${Layout.space.md} 0`,
          maxWidth: this.searchBoxFocus || this.searchBoxFilled ? '480px' : '280px',
          zIndex: Z_INDEXES.TEAMUP.SEARCH_BOX,
        };
      },
      loadedTenantTeamPermission: function () {
        return this.loaded('tenantTeamPermissions');
      },
    },
    methods: {
      addTenantUserToTenantTeam: async function (tenantUserId) {
        try {
          const payload = await {
            tenantUserId: tenantUserId,
            selected: true
          };
          const api = await new ApiTenantTeamTenantUser(this.tenantTeam.id);
          const res = await api.post(payload);
          await this.$store.commit(
            'teamup/push', { key: 'tenantTeamTenantUsers', data: res.data });
          await this.$store.commit('teamup/clear', 'tenantTeamTenantUserSkills');
          await this.$store.commit(
              'notification/setMessage', `Added ${this.getUserName(res.data)}`);
        } catch (error) {
          this.$store.commit('notification/setWarning', 'Failed to add user');
          console.error(error);
        }
      },
      clearSearchCache: function () {
        this.experts = [];
        this.isSearching = false;
        this.nPages = 0;
        this.page = 1;
      },
      deleteTenantTeamTenantUser: async function (tenantTeamTenantUserId) {
        try {
          const api = await new ApiTenantTeamTenantUser(this.tenantTeam.id, tenantTeamTenantUserId);
          const res = await api.delete();
          await this.$store.commit(
              'teamup/delete', { key: 'tenantTeamTenantUsers', data: res.data });
          await this.$store.commit('teamup/clear', 'tenantTeamTenantUserSkills');
          await this.$store.commit(
              'notification/setMessage', `Removed ${this.getUserName(res.data)}`);
        } catch (error) {
          this.$store.commit('notification/setWarning', 'Failed to remove user');
          console.error(error);
        }
      },
      getTenantTeamTenantUserId: function (tenantUserId) {
        const index = this.tenantTeamTenantUsers.findIndex(obj => obj.tenantUser.id === tenantUserId);
        return index < 0 ? null : this.tenantTeamTenantUsers[index].id;
      },
      goToTenantTeamSelectPageByError: function () {
        this.$store.commit(
            'notification/setWarning', "Can't search user for the team.");
        this.$router.push({ name: 'teamup-select' });
      },
      getUserName: function (tenantTeamTenantUser) {
        return `${tenantTeamTenantUser.tenantUser.user.firstName} ${tenantTeamTenantUser.tenantUser.user.lastName}`;
      },
      searchExpertByPersonName: function (input) {
        if (this.searchBySkill || input !== this.initInput) {
          this.$router.push({
            name: 'teamup-search',
            params: {
              code: this.tenantTeam.code,
            },
            query: {
              by: SEARCH_EXPERT_PARAMS.MODE.BY_PERSON_NAME,
              tag: input,
            },
          });
        }
      },
      searchExpertBySkillName: function (tags) {
        if (
          !this.searchBySkill ||
          JSON.stringify(this.initTags) !== JSON.stringify(tags)
        ) {
          this.$router.push({
            name: 'teamup-search',
            params: {
              code: this.tenantTeam.code,
            },
            query: {
              by: SEARCH_EXPERT_PARAMS.MODE.BY_SKILL_NAME,
              tag: tags,
            },
          });
        }
      },
      searchExpert: async function (searchInput, searchBySkill) {
        try {
          this.isSearching = await true;

          const payload = await {
            bySkill: searchBySkill,
            includeSelf: true,
            inputData: searchInput,
          };
          const query = await {
            page: this.page,
            pageSize: this.pageSize,
          };

          const api = await new ApiExpert();
          const res = await api.post(payload, query);
          this.nPages = await res.data.nPages;
          this.experts = await res.data.results;

          this.isSearching = await false;
        } catch (error) {
          this.clearSearchCache();
          this.$store.commit('notification/setWarning', 'Search failed');
          console.error(error);
        }
      },
    },
    watch: {
      loadedTenantTeamPermission: function (newVal, oldVal) {
        if (newVal && this.viewOnly) {
          this.goToTenantTeamSelectPageByError();
        }
      },
      windowWidth: function (newVal, oldVal) {
        this.verticalLayout = newVal < this.verticalLayoutWidthThresh;
      },
    },
  };
</script>
